import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { Input } from './ui/input';
import { Label } from './ui/label';
import InfoTip from './InfoTip';

// "Keep it current" settings shared by the upload card and the knowledge editors.
export const EMPTY_REVIEW_SCHEDULE = {
  every_months: '', next_due: '', remind_days_before: '14', due_action: 'review',
  notify: 'source_leaders', notify_user_id: '', escalate: true,
};

export const reviewScheduleFromItem = (schedule) => (schedule ? {
  every_months: schedule.every_months ? String(schedule.every_months) : '',
  next_due: schedule.every_months ? '' : (schedule.next_due || ''),
  remind_days_before: String(schedule.remind_days_before ?? 14),
  due_action: schedule.due_action || 'review',
  notify: schedule.notify || 'source_leaders',
  notify_user_id: schedule.notify_user_id || '',
  escalate: Boolean(schedule.escalate_after_days),
} : { ...EMPTY_REVIEW_SCHEDULE });

// null clears the schedule; the server works out the next due date from the interval.
export const reviewSchedulePayload = (value) => {
  if (!value || (!value.every_months && !value.next_due)) return null;
  return {
    every_months: value.every_months ? Number(value.every_months) : null,
    next_due: value.next_due || null,
    remind_days_before: Number(value.remind_days_before || 0),
    due_action: value.due_action || 'review',
    notify: value.notify || 'source_leaders',
    notify_user_id: value.notify === 'person' ? value.notify_user_id || null : null,
    escalate_after_days: value.escalate ? 7 : null,
  };
};

const addMonths = (isoDate, months) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.toISOString().slice(0, 10);
};

const selectClass = 'mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm';

const ReviewScheduleFields = ({ value, onChange, anchorDate, teamName }) => {
  const [people, setPeople] = useState(null);
  const set = (key, next) => onChange({ ...value, [key]: next });
  const scheduled = Boolean(value.every_months || value.next_due);
  const start = anchorDate || new Date().toISOString().slice(0, 10);
  const autoDue = value.every_months ? addMonths(start, Number(value.every_months)) : '';

  useEffect(() => {
    if (value.notify !== 'person' || people) return;
    axiosInstance.get('/users').then(({ data }) => setPeople(data || [])).catch(() => setPeople([]));
  }, [value.notify, people]);

  return <div className="col-span-full space-y-3 border-t border-slate-200 pt-3" data-testid="review-schedule-fields">
    <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-700">Keep it current</span>
      <InfoTip label="About review reminders">Qflow reminds the people who look after this document before it needs reviewing. Ordinary members never see these reminders.</InfoTip></div>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <label className="text-xs text-gray-600">Review every
        <select className={selectClass} value={value.every_months} onChange={(event) => onChange({ ...value, every_months: event.target.value, next_due: '' })}>
          <option value="">No schedule</option><option value="1">1 month</option><option value="3">3 months</option><option value="6">6 months</option><option value="12">12 months</option><option value="24">24 months</option>
        </select>
      </label>
      <div className="text-xs text-gray-600"><Label className="text-xs font-normal text-gray-600">Next review due</Label>
        {value.every_months ? <p className="mt-1 flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-2 text-sm text-gray-700" title={`Counted from ${anchorDate ? 'the last reviewed or published date' : 'today'}`}>{autoDue}</p>
          : <Input type="date" value={value.next_due} onChange={(event) => set('next_due', event.target.value)} className="mt-1 h-9 bg-white" aria-label="One-off review date" />}
      </div>
      <label className="text-xs text-gray-600">Remind
        <select className={selectClass} value={value.remind_days_before} onChange={(event) => set('remind_days_before', event.target.value)} disabled={!scheduled}>
          <option value="0">On the day</option><option value="7">7 days before</option><option value="14">14 days before</option><option value="30">30 days before</option>
        </select>
      </label>
      <label className="text-xs text-gray-600">When it's due
        <select className={selectClass} value={value.due_action} onChange={(event) => set('due_action', event.target.value)} disabled={!scheduled}>
          <option value="review">Review and confirm</option><option value="update">Update</option><option value="replace">Replace</option><option value="retire">Retire</option>
        </select>
      </label>
    </div>
    {scheduled && <div className="grid gap-3 md:grid-cols-2">
      <label className="text-xs text-gray-600">Notify
        <select className={selectClass} value={value.notify} onChange={(event) => set('notify', event.target.value)}>
          <option value="source_leaders">{teamName ? `Source Leaders of ${teamName}` : "The team's Source Leaders"} (default)</option>
          <option value="person">A specific person…</option>
        </select>
      </label>
      {value.notify === 'person' && <label className="text-xs text-gray-600">Person
        <select className={selectClass} value={value.notify_user_id} onChange={(event) => set('notify_user_id', event.target.value)} required>
          <option value="">{people === null ? 'Loading people…' : 'Choose a person'}</option>
          {(people || []).map((person) => <option key={person.id} value={person.id}>{person.name || person.email}</option>)}
        </select>
      </label>}
      <label className="col-span-full flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" className="h-4 w-4 accent-sky-600" checked={value.escalate} onChange={(event) => set('escalate', event.target.checked)} />
        If it's still not done 7 days after it's due, also tell the Tenant Admin
      </label>
      {!teamName && value.notify === 'source_leaders' && <p className="col-span-full text-xs text-amber-800">This document has no team yet, so reminders go to the Tenant Admin.</p>}
    </div>}
  </div>;
};

export default ReviewScheduleFields;
